import { CircularProgress, makeStyles, Typography } from '@material-ui/core';
import { IconButton, useConfirmation } from '@coolremote/shared';
import { Group as sdkGroup } from 'coolremote-sdk';
import clsx from 'clsx';
import _ from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { t } from 'ttag';
import { useStoreActions, useStoreState } from '../../models/RootStore';
import { AddIcon, ArrowBack } from '../../svgComponents';
import AddEditSchedule from './AddEditSchedule';
import useChurnZero from '@hooks/useChurnZero';
import ScheduleItem from './ScheduleItem';
import BacnetAddEditSchedule from '../../screens/Control/ControlComponents/BacnetSchedule/AddEditSchedule';
import {
  useBacnetSchedules,
  useBacnetScheduleNavigation,
  useBacnetScheduleTemplates,
} from '../../hooks/useBacnetSchedules';
import ModbusAddEditSchedule from '../../screens/Control/ControlComponents/ModbusSchedule/AddEditSchedule';
import { useModbusSchedules, useModbusScheduleNavigation } from '../../hooks/useModbusSchedules';
import { isModbusPowerMeterFallback } from '../../screens/Control/ControlComponents/ModbusSchedule/constants';

import styles from './scheduleList.style';
import { useGroupUniversalMembers } from '../../hooks/useGroupUniversalMembers';
interface CommonProps {
  hideSchedulesPanel: () => void;
  setOpenDeleteDialog?: (open: boolean) => void;
}

interface UnitScheduleListProps extends CommonProps {
  mode: 'unit';
  isGroup: boolean;
  item: any;
  allSchedules: any;
  setAllSchedules: (s: any) => void;
  addItemSchedule: any;
  deleteItemSchedule: any;
  isHalfCDegreeEnabled?: boolean;
  unitGroups?: any;
  allGroups?: any;
  timezoneOffset?: any;
}

interface SensorScheduleListProps extends CommonProps {
  mode: 'sensor';
  sensorId: string;
  allSchedules?: any;
  setAllSchedules?: (s: any) => void;
}

interface BacnetScheduleListProps extends CommonProps {
  mode: 'bacnet';
  bacnetUnit: any;
  unitGroups?: string[];
  isDesktop?: boolean;
  onBacnetSchedulesUpdate?: (schedules: any[]) => void;
}

interface ModbusScheduleListProps extends CommonProps {
  mode: 'modbus';
  modbusUnit: any;
  unitGroups?: string[];
  isDesktop?: boolean;
  onModbusSchedulesUpdate?: (schedules: any[]) => void;
}

type Props =
  | UnitScheduleListProps
  | SensorScheduleListProps
  | BacnetScheduleListProps
  | ModbusScheduleListProps;


const getGroupsSchedules = (groupIds: string[]) =>
  Promise.allSettled(groupIds.map((groupId: string) => sdkGroup.getGroupSchdeules(groupId)))
    .then((resp: any) => {
      let scheduleObjects: any = {};
      for (const result of resp) {
        const { value: groupSchedulesMap = {} } = result;
        scheduleObjects = { ...scheduleObjects, ...groupSchedulesMap };
      }
      return scheduleObjects;
    });

const ScheduleList: React.FC<Props> = (props) => {
  const useStyles = makeStyles(styles);
  const classes = useStyles();
  const confirm = useConfirmation();
  const { mode, hideSchedulesPanel } = props;
  const isUnit = mode === 'unit';
  const isSensor = mode === 'sensor';
  const isBacnet = mode === 'bacnet';
  const isModbus = mode === 'modbus';

  // ---- Common state ----
  const [openAddEditSchedule, handleAddEditSchedule] = useState<string>('');

  // ---- Unit-only state ----
  const [disabledGroupSchedules, setDisabledGroupSchedules] = useState<any[]>(
    []
  );
  const [disabledSystemScheduleArr, setDisabledSystemScheduleArr] = useState<
    any[]
  >([]);
  const [groupSchedules, setGroupSchedules] = useState<any[]>([]);
  const refetchCleanupsRef = useRef<{ [key: string]: () => void }>({});

  const [universalGroupSchedules, setUniversalGroupSchedules] = useState<any>({});

  // ---- Sensor-only state ----
  const [sensorSchedules, setSensorSchedules] = useState<any>({});
  const [sensor, setSensor] = useState<any>(null);

  // ---- Store ----
  const { addMessage } = useStoreActions((action) => action.errorMessage);
  const { updateSchedule, deleteSchedule, getSchedule } = useStoreActions(
    (actions) => actions.schedules
  );
  const { getSystemSchedules } = useStoreActions((actions) => actions.systems);
  const getSensorSchedulesFn = useStoreActions(
    (action) => action.sensors.getSensorSchedules
  );
  const deleteScheduleFromSensorEntity = useStoreActions(
    (action) => action.sensors.deleteScheduleFromSensorEntity
  );
  const addScheduleToSensorEntity = useStoreActions(
    (action) => action.sensors.addScheduleToSensorEntity
  );
  const getSensorFn = useStoreState((state) => state.sensors.getSensor);
  const { trackEvent } = useChurnZero();

  // ---- Shared: the customer-wide schedules map, kept in sync with the
  // page's badges/icons (e.g. SensorPanel, UnitPanel) so toggling/adding/
  // deleting a schedule here is reflected there without a full refetch.
  const { allSchedules = {}, setAllSchedules } = props as
    | UnitScheduleListProps
    | SensorScheduleListProps;

  // ---- Unit-only derived values ----
  const unitProps = isUnit
    ? (props as UnitScheduleListProps)
    : ({} as Partial<UnitScheduleListProps>);
  const {
    item = {},
    addItemSchedule,
    deleteItemSchedule,
    isGroup = false,
    isHalfCDegreeEnabled = false,
  } = unitProps;
  const {
    id: itemId,
    schedules: itemScheduleIds = [],
    system = '',
    capabilityFlags = {},
    permissions: unitPermissions = {},
  } = item;
  const { canUpdateSchedules, canCreateSchedules, canDeleteSchedules } =
    unitPermissions;
  const { members: groupUniversalMembers } = useGroupUniversalMembers({
    group: isUnit && isGroup ? item : null,
  });
  const { enableSetpointControl = false } = capabilityFlags;
  const itemSchdules = [
    ...new Set([...(itemScheduleIds as string[]), ...groupSchedules]),
  ];

  // ---- Bacnet-only derived values ----
  const bacnetProps = isBacnet
    ? (props as BacnetScheduleListProps)
    : ({} as Partial<BacnetScheduleListProps>);
  const { bacnetUnit, onBacnetSchedulesUpdate } = bacnetProps;
  const bacnetUnitId = bacnetUnit?.id;
  const { permissions: bacnetPermissions = {} } = bacnetUnit || {};

  // ---- Modbus-only derived values ----
  const modbusProps = isModbus
    ? (props as ModbusScheduleListProps)
    : ({} as Partial<ModbusScheduleListProps>);
  const { modbusUnit, onModbusSchedulesUpdate } = modbusProps;
  const modbusUnitId = modbusUnit?.id;
  const { permissions: modbusPermissions = {} } = modbusUnit || {};

  const getIsDesktop = () => {
    if (isBacnet) {
      return bacnetProps.isDesktop ?? false;
    }
    if (isModbus) {
      return modbusProps.isDesktop ?? false;
    }
    return false;
  };
  const isDesktop = getIsDesktop();

  // ---- Sensor-only derived values ----
  const sensorId = isSensor
    ? (props as SensorScheduleListProps).sensorId
    : undefined;
  const { permissions: sensorPermissions = {} } = sensor || {};

  const {
    schedulesArray,
    isLoading: isBacnetLoading,
    deleteSchedule: deleteBacnetSchedule,
    toggleScheduleState,
    refetch: refetchBacnetSchedules,
    error: bacnetError,
  } = useBacnetSchedules(bacnetUnitId);

  const {
    selectedSchedule,
    setSelectedSchedule,
  } = useBacnetScheduleNavigation(hideSchedulesPanel);

  const { uiTemplate, paramTemplate } =
    useBacnetScheduleTemplates(bacnetUnit);

  useEffect(() => {
    if (isBacnet && onBacnetSchedulesUpdate && schedulesArray) {
      // Pass the full schedule objects (not just ids) so toggling a schedule's
      // isDisabled state is reflected immediately in the list badge — passing
      // ids alone made toggle a no-op here (same id set), leaving the list's
      // copy of the schedules stale until the page was refreshed.
      onBacnetSchedulesUpdate(schedulesArray);
    }
  }, [schedulesArray]);

  const {
    schedulesArray: modbusSchedulesArray,
    isLoading: isModbusLoading,
    deleteSchedule: deleteModbusSchedule,
    toggleScheduleState: toggleModbusScheduleState,
    mergeSchedule: mergeModbusSchedule,
  } = useModbusSchedules(modbusUnitId);

  const {
    selectedSchedule: selectedModbusSchedule,
    setSelectedSchedule: setSelectedModbusSchedule,
  } = useModbusScheduleNavigation(hideSchedulesPanel);

  useEffect(() => {
    if (isModbus && onModbusSchedulesUpdate && modbusSchedulesArray) {
      onModbusSchedulesUpdate(modbusSchedulesArray);
    }
  }, [modbusSchedulesArray]);

  const getCanAdd = () => {
    if (isUnit) {
      return canCreateSchedules;
    }
    if (isSensor) {
      return sensorPermissions.canCreateSchedules;
    }
    if (isBacnet) {
      return bacnetPermissions.canCreateSchedules;
    }
    return modbusPermissions.canCreateSchedules && !isModbusPowerMeterFallback(modbusUnit);
  };
  const canAdd = getCanAdd();

  // ---- Effects ----

  // Unit: fetch group + system schedules
  useEffect(() => {
    if (!isUnit || _.isEmpty(item)) {
      return;
    }
    if (!isGroup) {
      system &&
        getSystemSchedules(system).then((res: any) => {
          setDisabledSystemScheduleArr(
            Object.values(res).map((s: any) => s.id)
          );
          // Merge in the actual schedule objects, not just their ids. On
          // mobile's per-entity schedule screen, `allSchedules` only holds
          // this unit's own schedules (fetched separately from the system's),
          // so without this merge ScheduleItem's `allSchedules[scheduleId]`
          // lookup comes back undefined and it silently renders nothing -
          // on desktop this is a no-op since the customer-wide map already
          // has them.
          setAllSchedules?.((prev: any) => ({ ...prev, ...res }));
        });
      if (item?.groups) {
        getGroupsSchedules(item.groups).then((scheduleObjects: any) => {
          setDisabledGroupSchedules(Object.keys(scheduleObjects));
          // Same reasoning as the system-schedules merge above.
          setAllSchedules?.((prev: any) => ({ ...prev, ...scheduleObjects }));
        });
      }
    }
  }, []);

  useEffect(() => {
    if (!isUnit || !isGroup) {
      return;
    }
    sdkGroup.getGroupSchdeules(item.id).then((resp: any) => {
      setGroupSchedules(Object.keys(resp));
    });
  }, [isGroup]);


  const universalUnitGroups: string[] =
    (isBacnet || isModbus) ? (props as BacnetScheduleListProps | ModbusScheduleListProps).unitGroups || [] : [];
  const universalUnitGroupsKey = universalUnitGroups.join(',');
  useEffect(() => {
    setUniversalGroupSchedules({});
    if (!universalUnitGroups.length) {
      return;
    }
    getGroupsSchedules(universalUnitGroups).then(setUniversalGroupSchedules);
  }, [universalUnitGroupsKey]);

  // Unit: cleanup refetch timeouts on unmount
  useEffect(() => {
    if (!isUnit) {
      return;
    }
    return () => {
      Object.values(refetchCleanupsRef.current).forEach((cleanup) => cleanup());
      refetchCleanupsRef.current = {};
    };
  }, []);

  // Sensor: fetch sensor data + schedules
  useEffect(() => {
    if (isUnit || !sensorId) {
      return;
    }
    setSensor(getSensorFn(sensorId));
    getSensorSchedulesFn(sensorId)
      .then((schedules: any) => setSensorSchedules(schedules))
      .catch(() => {});
  }, []);

  // ---- Common handlers ----
  const changeScheduleActiveState = (e: any, id: string) => {
    const isDisabled = !e.target.checked;
    updateSchedule({ id, data: { isDisabled } })
      .then(() => {
        if (isUnit) {
          setAllSchedules!({
            ...allSchedules,
            [id]: { ...allSchedules[id], isDisabled },
          });
        } else {
          setSensorSchedules((prev: any) => ({
            ...prev,
            [id]: { ...prev[id], isDisabled },
          }));
          // Also keep the customer-wide map in sync, so a sensor's
          // "has schedule" badge (which reads that map, not sensorSchedules)
          // reflects the toggle immediately instead of only after a refetch.
          setAllSchedules?.({
            ...allSchedules,
            [id]: { ...allSchedules[id], isDisabled },
          });
        }
      })
      .catch((err: any) => addMessage({ message: err.message }));
  };

  const changeUniversalGroupScheduleActiveState = (e: any, id: string) => {
    const isDisabled = !e.target.checked;
    updateSchedule({ id, data: { isDisabled } })
      .then(() => {
        setUniversalGroupSchedules((prev: any) => ({
          ...prev,
          [id]: { ...prev[id], isDisabled },
        }));
      })
      .catch((err: any) => addMessage({ message: err.message }));
  };

  const handleScheduleDeleteClick = async (schedule: any, type: string) => {
    const userResponse = await confirm({
      title: t`Delete Schedule`,
      bodyMessage:
        t`Are you sure you want to delete ` + schedule?.name + ` schedule?`,
      confirmText: t`Delete`,
      cancelText: t`Cancel`,
      white: true
    });

    if (userResponse) {
      await deleteSelectedSchedule(schedule.id, type);
    }
  };

  // ---- Unit-only handlers ----

  const saveUnitScheduleData = (scheduleId: string, updatedData: any) => {
    allSchedules[scheduleId] = updatedData;
    setAllSchedules!({ ...allSchedules });

    if (refetchCleanupsRef.current[scheduleId]) {
      refetchCleanupsRef.current[scheduleId]();
    }

    let timeoutId: NodeJS.Timeout | null = null;
    let attemptCount = 0;
    let cancelled = false;
    const maxAttempts = 10;

    const refetchSchedule = () => {
      if (cancelled || attemptCount >= maxAttempts) {
        return;
      }
      attemptCount++;
      timeoutId = setTimeout(() => {
        getSchedule(scheduleId)
          .then((refreshed: any) => {
            if (cancelled) return;
            allSchedules[scheduleId] = refreshed;
            setAllSchedules!({ ...allSchedules });
            if (refreshed.isSynced === 1 && attemptCount < maxAttempts) {
              refetchSchedule();
            } else if (attemptCount >= maxAttempts) {
              console.warn(
                `Max refetch attempts (${maxAttempts}) reached for schedule ${scheduleId}`
              );
            }
          })
          .catch((err: any) =>
            console.error(`Failed to refetch schedule ${scheduleId}:`, err)
          );
      }, 2000);
    };

    refetchSchedule();
    refetchCleanupsRef.current[scheduleId] = () => {
      cancelled = true;
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    };
  };

  const deleteUnitSchedule = (scheduleId: string) => {
    deleteSchedule(scheduleId)
      .then(() => deleteItemSchedule(isGroup, itemId, scheduleId))
      .catch((err: any) => addMessage({ message: err.message }))
      .finally(() => {
        if (isGroup) {
          sdkGroup
            .getGroupSchdeules(item.id)
            .then((resp: any) => setGroupSchedules(Object.keys(resp)));
        }
      });
  };

  // ---- Sensor-only handlers ----

  const saveSensorScheduleData = (scheduleId: string, updatedData: any) => {
    setSensorSchedules((prev: any) => ({ ...prev, [scheduleId]: updatedData }));
    // Keep the customer-wide map in sync too - see changeScheduleActiveState.
    setAllSchedules?.({ ...allSchedules, [scheduleId]: updatedData });
  };

  const addSensorScheduleLocally = (schedule: any) => {
    addScheduleToSensorEntity({ sensorId: sensor.id, scheduleId: schedule.id });
    setSensorSchedules((prev: any) => ({ ...prev, [schedule.id]: schedule }));
    setAllSchedules?.({ ...allSchedules, [schedule.id]: schedule });
  };

  const deleteSensorSchedule = (scheduleId: string) => {
    deleteSchedule(scheduleId)
      .then(() => {
        setSensorSchedules((prev: any) => {
          const next = { ...prev };
          delete next[scheduleId];
          return next;
        });
        deleteScheduleFromSensorEntity({ sensorId: sensor.id, scheduleId });
        if (setAllSchedules) {
          const next = { ...allSchedules };
          delete next[scheduleId];
          setAllSchedules(next);
        }
      })
      .catch((err: any) => addMessage({ message: err.message }));
  };

  const deleteSelectedSchedule = (scheduleId: string, type: string) => {
    switch (type) {
      case 'unit':
        deleteUnitSchedule(scheduleId);
        break;
      case 'sensor':
        deleteSensorSchedule(scheduleId);
        break;
      case 'bacnet':
        deleteBacnetSchedule(scheduleId);
        refetchBacnetSchedules({ silent: true });
        break;
      case 'modbus':
        deleteModbusSchedule(scheduleId);
        break;
      default:
        break;
    }
  };

  // ---- Bacnet-only handlers ----
  const handleToggleBacnetSchedule = async (schedule: any) => {
    await toggleScheduleState(schedule);
  };

  // ---- Modbus-only handlers ----
  const handleToggleModbusSchedule = async (schedule: any) => {
    await toggleModbusScheduleState(schedule);
  };

  // ---- Add button ----

  const handleAddButtonClick = () => {
    handleAddEditSchedule('new');
    if (isUnit) {
      const eventType = isGroup ? 'AddGroupSchedule' : 'AddUnitSchedule';
      const description = isGroup
        ? 'The user clicked on add schedule on the Group level'
        : 'The user clicked on add schedule on the IDU level';
      trackEvent(eventType, description);
    }
  };

  // ---- Render ----

  const isEmpty = isUnit
    ? itemSchdules.length === 0 &&
      disabledGroupSchedules.length === 0 &&
      disabledSystemScheduleArr.length === 0
    : isSensor
    ? Object.values(sensorSchedules).length === 0
    : isBacnet
    ? schedulesArray.length === 0 && _.isEmpty(universalGroupSchedules)
    : modbusSchedulesArray.length === 0 && _.isEmpty(universalGroupSchedules);

  const unitItemProps = {
    canUpdateSchedules,
    canDeleteSchedules,
    isGroup,
    enableSetpointControl,
    isHalfCDegreeEnabled,
    onEdit: handleAddEditSchedule,
    onDeleteClick: handleScheduleDeleteClick,
    onToggle: changeScheduleActiveState,
  };

  const renderScheduleItems = () => {
    if (isUnit) {
      return (
        <>
          {itemSchdules.map((scheduleId: string) => (
            <ScheduleItem
              type="unit"
              key={`schedule-${scheduleId}`}
              scheduleId={scheduleId}
              schedule={allSchedules[scheduleId]}
              {...unitItemProps}
            />
          ))}
          {disabledGroupSchedules
            .filter((scheduleId: string) => !itemSchdules.includes(scheduleId))
            .map((scheduleId: string) => (
            <ScheduleItem
              type="group"
              key={`schedule-${scheduleId}`}
              scheduleId={scheduleId}
              schedule={allSchedules[scheduleId]}
              notEditable
              {...unitItemProps}
            />
          ))}
          {disabledSystemScheduleArr.map((scheduleId: string) => (
            <ScheduleItem
              type="system"
              key={`schedule-${scheduleId}`}
              scheduleId={scheduleId}
              schedule={allSchedules[scheduleId]}
              notEditable
              {...unitItemProps}
            />
          ))}
        </>
      );
    }

    if (isSensor) {
      return Object.values(sensorSchedules).map((schedule: any) => (
        <ScheduleItem
          type="sensor"
          key={`schedule-${(schedule as any).id}`}
          schedule={schedule}
          sensor={sensor}
          onEdit={handleAddEditSchedule}
          onDeleteClick={handleScheduleDeleteClick}
          onToggle={changeScheduleActiveState}
        />
      ));
    }

    // Group schedules are read-only here,
    const universalGroupScheduleItems = Object.values(universalGroupSchedules).map((schedule: any) => (
      <ScheduleItem
        type="group"
        key={`group-schedule-${schedule.id}`}
        scheduleId={schedule.id}
        schedule={schedule}
        notEditable
        isGroup={false}
        enableSetpointControl={false}
        onEdit={handleAddEditSchedule}
        onDeleteClick={handleScheduleDeleteClick}
        onToggle={changeUniversalGroupScheduleActiveState}
      />
    ));

    if (isModbus) {
      return [...modbusSchedulesArray.map((schedule: any) => (
        <ScheduleItem
          key={schedule.id}
          type="modbus"
          schedule={schedule}
          onEdit={() => {
            setSelectedModbusSchedule(schedule);
            handleAddEditSchedule(schedule.id);
          }}
          onDeleteClick={handleScheduleDeleteClick}
          onToggle={() => handleToggleModbusSchedule(schedule)}
          isDesktop={isDesktop}
        />
      )), ...universalGroupScheduleItems];
    }

    return [...schedulesArray.map((schedule: any) => (
      <ScheduleItem
        key={schedule.id}
        type="bacnet"
        schedule={schedule}
        onEdit={() => {
          setSelectedSchedule(schedule);
          handleAddEditSchedule(schedule.id);
        }}
        onDeleteClick={handleScheduleDeleteClick}
        onToggle={() => handleToggleBacnetSchedule(schedule)}
        isDesktop={isDesktop}
      />
    )), ...universalGroupScheduleItems];
  };

  const renderAddEditSchedule = () => {
    if (!openAddEditSchedule) {
      return null;
    }

    if (isBacnet) {
      return (
        <BacnetAddEditSchedule
          bacnetUnit={bacnetUnit}
          scheduleData={selectedSchedule}
          onClose={() => {
            handleAddEditSchedule('');
            setSelectedSchedule(null);
            refetchBacnetSchedules({ silent: true });
          }}
          uiTemplate={uiTemplate}
          paramTemplate={paramTemplate}
          isDesktop={isDesktop}
        />
      );
    }

    if (isModbus) {
      return (
        <ModbusAddEditSchedule
          modbusUnit={modbusUnit}
          scheduleData={selectedModbusSchedule}
          onClose={(savedSchedule?: any) => {
            handleAddEditSchedule('');
            setSelectedModbusSchedule(null);
            // Only a successful save passes the schedule back - a Cancel has
            // nothing to merge, so there's nothing to do here.
            if (savedSchedule) {
              mergeModbusSchedule(savedSchedule);
            }
          }}
          isDesktop={isDesktop}
        />
      );
    }

    if (isUnit) {
      return (
        <AddEditSchedule
          editMode={openAddEditSchedule !== 'new'}
          itemId={itemId}
          isGroup={isGroup}
          scheduleData={{ ...allSchedules[openAddEditSchedule] }}
          hideSchedulesPanel={hideSchedulesPanel}
          closeAddEditSchedule={() => handleAddEditSchedule('')}
          saveScheduleData={saveUnitScheduleData}
          addItemSchedule={addItemSchedule}
          isHalfCDegreeEnabled={isHalfCDegreeEnabled}
          universalMembers={groupUniversalMembers}
        />
      );
    }

    return (
      <AddEditSchedule
        editMode={openAddEditSchedule !== 'new'}
        sensor={sensor}
        scheduleData={sensorSchedules[openAddEditSchedule] || {}}
        hideSchedulesPanel={hideSchedulesPanel}
        closeAddEditSchedule={() => handleAddEditSchedule('')}
        saveScheduleData={saveSensorScheduleData}
        addScheduleLocaly={addSensorScheduleLocally}
      />
    );
  };

  return (
    <div className={classes.screenContainer}>
      {!openAddEditSchedule && (
        <>
          <div className={classes.headerStyle}>
            <IconButton
              onClick={hideSchedulesPanel}
              className={classes.bigIconBtnStyle}
            >
              <ArrowBack />
            </IconButton>
            <Typography
              style={{ color: '#fff', fontSize: 25 }}
            >{t`Schedules`}</Typography>
            <IconButton
              disabled={!canAdd}
              onClick={handleAddButtonClick}
              className={classes.bigIconBtnStyle}
            >
              <AddIcon />
            </IconButton>
          </div>
          <div
            className={clsx(classes.schedulesContainer, {
              [classes.schedulesContainerMobile]: !isDesktop,
            })}
          >
            {(isBacnet && isBacnetLoading) || (isModbus && isModbusLoading) ? (
              <div className={classes.loadingContainer}>
                <CircularProgress />
              </div>
            ) : isEmpty ? (
              <Typography className={classes.noSchedulesStyle}>
                {t`There are no schedules to show`}
              </Typography>
            ) : (
              <>{renderScheduleItems()}</>
            )}
          </div>
        </>
      )}
      {renderAddEditSchedule()}
    </div>
  );
};

export default ScheduleList;
